'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Send, Loader2, Info, ChevronRight, CheckCircle2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { StoreResourcePicker, OrderResourcePicker } from './resource-pickers';
import { resolveOrderAutofill, ORDER_DERIVED_FIELD_NAMES, type PickedOrder } from './resource-pickers/order-autofill';
import { useDispatchEvent } from '@/lib/api/events';

// ---------------------------------------------------------------------------
// Event registry: every simulatable event with its fields and defaults
// ---------------------------------------------------------------------------

interface FieldDef {
  name: string;
  labelKey: string;
  type: 'text' | 'number' | 'select' | 'store' | 'order';
  required?: boolean;
  placeholder?: string;
  defaultValue?: string;
  options?: { labelKey: string; value: string }[];
  dependsOn?: string;
  hidden?: boolean;
}

interface EventDef {
  labelKey: string;
  descriptionKey: string;
  category: string;
  namespace: string;
  eventName: string;
  fields: FieldDef[];
}

const EVENT_REGISTRY: EventDef[] = [
  // -- Orders --
  {
    labelKey: 'simulator.events.orderPaid.label',
    descriptionKey: 'simulator.events.orderPaid.description',
    category: 'Orders',
    namespace: 'order',
    eventName: 'paid',
    fields: [
      { name: 'storeId', labelKey: 'simulator.fields.store', type: 'store', required: true },
      { name: 'orderId', labelKey: 'simulator.fields.order', type: 'order', required: true, dependsOn: 'storeId' },
      { name: 'paymentId', labelKey: 'simulator.fields.paymentId', type: 'text', required: true, placeholder: 'pay_xxx', defaultValue: 'pay_test_' + Date.now().toString(36) },
      { name: 'isReturning', labelKey: 'simulator.fields.returningCustomer', type: 'select', required: true, options: [{ labelKey: 'simulator.fields.no', value: 'false' }, { labelKey: 'simulator.fields.yes', value: 'true' }], defaultValue: 'false' },
      { name: 'cartToken', labelKey: 'simulator.fields.cartToken', type: 'text', placeholder: 'Optional', defaultValue: '' },
    ],
  },
  {
    labelKey: 'simulator.events.orderCreated.label',
    descriptionKey: 'simulator.events.orderCreated.description',
    category: 'Orders',
    namespace: 'order',
    eventName: 'created',
    fields: [
      { name: 'storeId', labelKey: 'simulator.fields.store', type: 'store', required: true },
      { name: 'orderId', labelKey: 'simulator.fields.order', type: 'order', required: true, dependsOn: 'storeId' },
      { name: 'customerId', labelKey: 'simulator.fields.customerId', type: 'text', required: true, placeholder: 'cus_xxx' },
      { name: 'checkoutSessionId', labelKey: 'simulator.fields.checkoutSessionId', type: 'text', required: true, placeholder: 'cs_xxx' },
      { name: 'draft', labelKey: 'simulator.fields.draft', type: 'select', options: [{ labelKey: 'simulator.fields.no', value: 'false' }, { labelKey: 'simulator.fields.yes', value: 'true' }], defaultValue: 'false' },
    ],
  },
  {
    labelKey: 'simulator.events.orderRefunded.label',
    descriptionKey: 'simulator.events.orderRefunded.description',
    category: 'Orders',
    namespace: 'order',
    eventName: 'refunded',
    fields: [
      { name: 'storeId', labelKey: 'simulator.fields.store', type: 'store', required: true },
      { name: 'orderId', labelKey: 'simulator.fields.order', type: 'order', required: true, dependsOn: 'storeId' },
      { name: 'refundAmount', labelKey: 'simulator.fields.refundAmount', type: 'number', placeholder: 'Optional' },
    ],
  },
  {
    labelKey: 'simulator.events.orderFailed.label',
    descriptionKey: 'simulator.events.orderFailed.description',
    category: 'Orders',
    namespace: 'order',
    eventName: 'failed',
    fields: [
      { name: 'storeId', labelKey: 'simulator.fields.store', type: 'store', required: true },
      { name: 'orderId', labelKey: 'simulator.fields.order', type: 'order', required: true, dependsOn: 'storeId' },
      { name: 'customerId', labelKey: 'simulator.fields.customerId', type: 'text', required: true, placeholder: 'cus_xxx' },
      { name: 'paymentId', labelKey: 'simulator.fields.paymentId', type: 'text', required: true, placeholder: 'pay_xxx' },
    ],
  },

  // -- Checkout --
  {
    labelKey: 'simulator.events.checkoutInitiated.label',
    descriptionKey: 'simulator.events.checkoutInitiated.description',
    category: 'Checkout',
    namespace: 'checkout',
    eventName: 'initiated',
    fields: [
      { name: 'storeId', labelKey: 'simulator.fields.store', type: 'store', required: true },
      { name: 'checkoutSessionId', labelKey: 'simulator.fields.checkoutSessionId', type: 'text', required: true, placeholder: 'cs_xxx' },
      { name: 'customerId', labelKey: 'simulator.fields.customerId', type: 'text', required: true, placeholder: 'cus_xxx' },
    ],
  },
  {
    labelKey: 'simulator.events.checkoutEmailValidated.label',
    descriptionKey: 'simulator.events.checkoutEmailValidated.description',
    category: 'Checkout',
    namespace: 'checkout',
    eventName: 'emailValidated',
    fields: [
      { name: 'storeId', labelKey: 'simulator.fields.store', type: 'store', required: true },
      { name: 'checkoutSessionId', labelKey: 'simulator.fields.checkoutSessionId', type: 'text', required: true, placeholder: 'cs_xxx' },
      { name: 'customerId', labelKey: 'simulator.fields.customerId', type: 'text', required: true, placeholder: 'cus_xxx' },
    ],
  },

  // -- Subscriptions --
  {
    labelKey: 'simulator.events.subscriptionCreated.label',
    descriptionKey: 'simulator.events.subscriptionCreated.description',
    category: 'Subscriptions',
    namespace: 'subscription',
    eventName: 'created',
    fields: [
      { name: 'storeId', labelKey: 'simulator.fields.store', type: 'store', required: true },
      { name: 'subscriptionId', labelKey: 'simulator.fields.subscriptionId', type: 'text', required: true, placeholder: 'sub_xxx' },
    ],
  },
  {
    labelKey: 'simulator.events.subscriptionCanceled.label',
    descriptionKey: 'simulator.events.subscriptionCanceled.description',
    category: 'Subscriptions',
    namespace: 'subscription',
    eventName: 'canceled',
    fields: [
      { name: 'storeId', labelKey: 'simulator.fields.store', type: 'store', required: true },
      { name: 'subscriptionId', labelKey: 'simulator.fields.subscriptionId', type: 'text', required: true, placeholder: 'sub_xxx' },
    ],
  },
  {
    labelKey: 'simulator.events.subscriptionPaused.label',
    descriptionKey: 'simulator.events.subscriptionPaused.description',
    category: 'Subscriptions',
    namespace: 'subscription',
    eventName: 'paused',
    fields: [
      { name: 'storeId', labelKey: 'simulator.fields.store', type: 'store', required: true },
      { name: 'subscriptionId', labelKey: 'simulator.fields.subscriptionId', type: 'text', required: true, placeholder: 'sub_xxx' },
    ],
  },
  {
    labelKey: 'simulator.events.subscriptionResumed.label',
    descriptionKey: 'simulator.events.subscriptionResumed.description',
    category: 'Subscriptions',
    namespace: 'subscription',
    eventName: 'resumed',
    fields: [
      { name: 'storeId', labelKey: 'simulator.fields.store', type: 'store', required: true },
      { name: 'subscriptionId', labelKey: 'simulator.fields.subscriptionId', type: 'text', required: true, placeholder: 'sub_xxx' },
    ],
  },
  {
    labelKey: 'simulator.events.rebillUpcoming.label',
    descriptionKey: 'simulator.events.rebillUpcoming.description',
    category: 'Subscriptions',
    namespace: 'subscription',
    eventName: 'rebillUpcoming',
    fields: [
      { name: 'storeId', labelKey: 'simulator.fields.store', type: 'store', required: true },
      { name: 'subscriptionId', labelKey: 'simulator.fields.subscriptionId', type: 'text', required: true, placeholder: 'sub_xxx' },
    ],
  },
  {
    labelKey: 'simulator.events.rebillSucceeded.label',
    descriptionKey: 'simulator.events.rebillSucceeded.description',
    category: 'Subscriptions',
    namespace: 'subscription',
    eventName: 'rebillSucceeded',
    fields: [
      { name: 'storeId', labelKey: 'simulator.fields.store', type: 'store', required: true },
      { name: 'subscriptionId', labelKey: 'simulator.fields.subscriptionId', type: 'text', required: true, placeholder: 'sub_xxx' },
      { name: 'customerId', labelKey: 'simulator.fields.customerId', type: 'text', required: true, placeholder: 'cus_xxx' },
      { name: 'currency', labelKey: 'simulator.fields.currency', type: 'text', required: true, placeholder: 'USD', defaultValue: 'USD' },
    ],
  },
  {
    labelKey: 'simulator.events.rebillDeclined.label',
    descriptionKey: 'simulator.events.rebillDeclined.description',
    category: 'Subscriptions',
    namespace: 'subscription',
    eventName: 'rebillDeclined',
    fields: [
      { name: 'storeId', labelKey: 'simulator.fields.store', type: 'store', required: true },
      { name: 'subscriptionId', labelKey: 'simulator.fields.subscriptionId', type: 'text', required: true, placeholder: 'sub_xxx' },
      { name: 'customerId', labelKey: 'simulator.fields.customerId', type: 'text', required: true, placeholder: 'cus_xxx' },
      { name: 'currency', labelKey: 'simulator.fields.currency', type: 'text', required: true, placeholder: 'USD', defaultValue: 'USD' },
    ],
  },

  // -- Club --
  {
    labelKey: 'simulator.events.membershipActivated.label',
    descriptionKey: 'simulator.events.membershipActivated.description',
    category: 'Club',
    namespace: 'club',
    eventName: 'membershipActivated',
    fields: [
      { name: 'storeId', labelKey: 'simulator.fields.store', type: 'store', required: true },
      { name: 'clubId', labelKey: 'simulator.fields.clubId', type: 'text', required: true, placeholder: 'club_xxx' },
      { name: 'customerId', labelKey: 'simulator.fields.customerId', type: 'text', required: true, placeholder: 'cus_xxx' },
    ],
  },
  {
    labelKey: 'simulator.events.membershipDeactivated.label',
    descriptionKey: 'simulator.events.membershipDeactivated.description',
    category: 'Club',
    namespace: 'club',
    eventName: 'membershipDeactivated',
    fields: [
      { name: 'storeId', labelKey: 'simulator.fields.store', type: 'store', required: true },
      { name: 'clubId', labelKey: 'simulator.fields.clubId', type: 'text', required: true, placeholder: 'club_xxx' },
      { name: 'customerId', labelKey: 'simulator.fields.customerId', type: 'text', required: true, placeholder: 'cus_xxx' },
    ],
  },
];

const CATEGORIES = [...new Set(EVENT_REGISTRY.map((e) => e.category))];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface EventSimulatorProps {
  onEventSent?: () => void;
}

export function EventSimulator({ onEventSent }: EventSimulatorProps) {
  const { t } = useTranslation('externalRequests');
  const { toast } = useToast();
  const dispatchEventMutation = useDispatchEvent();

  const [selectedCategory, setSelectedCategory] = React.useState(CATEGORIES[0]);
  const [selectedEventIdx, setSelectedEventIdx] = React.useState(0);
  const [formData, setFormData] = React.useState<Record<string, any>>({});
  const [formErrors, setFormErrors] = React.useState<Record<string, string>>({});
  const [lastSentEvent, setLastSentEvent] = React.useState<string | null>(null);

  const eventsInCategory = EVENT_REGISTRY.filter((e) => e.category === selectedCategory);
  const eventDef = eventsInCategory[selectedEventIdx] || eventsInCategory[0];

  React.useEffect(() => {
    setSelectedEventIdx(0);
  }, [selectedCategory]);

  React.useEffect(() => {
    if (!eventDef) return;
    const defaults: Record<string, any> = {};
    for (const field of eventDef.fields) {
      if (field.defaultValue !== undefined) defaults[field.name] = field.defaultValue;
    }
    setFormData(defaults);
    setFormErrors({});
    setLastSentEvent(null);
  }, [selectedCategory, selectedEventIdx]);

  const handleFieldChange = (name: string, value: string) => {
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'storeId') {
        for (const f of eventDef.fields) {
          if ((f.dependsOn === 'storeId' && f.type === 'order') || ORDER_DERIVED_FIELD_NAMES.includes(f.name)) {
            next[f.name] = '';
          }
        }
      }
      return next;
    });
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const copy = { ...prev };
        delete copy[name];
        return copy;
      });
    }
  };

  const handleOrderPick = (field: FieldDef, order: PickedOrder) => {
    const autofill = resolveOrderAutofill(order, field.name);
    const declaredFields = new Set(eventDef.fields.map((f) => f.name));
    for (const [name, value] of Object.entries(autofill)) {
      if (declaredFields.has(name)) {
        handleFieldChange(name, value);
      }
    }
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    for (const field of eventDef.fields) {
      if (field.required && !formData[field.name]) {
        errors[field.name] = t('simulator.fieldRequired', { label: t(field.labelKey) });
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const payload: Record<string, any> = {};
    for (const field of eventDef.fields) {
      const val = formData[field.name];
      if (val === undefined || val === '') continue;
      if (field.type === 'number') {
        payload[field.name] = Number(val);
      } else {
        payload[field.name] = val;
      }
    }

    if (eventDef.namespace === 'order' && eventDef.eventName === 'paid' && !payload.lineItems) {
      payload.lineItems = [
        { productId: 'prod_test', productName: 'Test Product', variantName: 'Default', sku: 'TEST-001', unitAmount: 4999, quantity: 1 },
      ];
    }

    try {
      await dispatchEventMutation.mutateAsync({
        namespace: eventDef.namespace,
        name: eventDef.eventName,
        data: payload,
      });
      setLastSentEvent(`${eventDef.namespace}/${eventDef.eventName}`);
      onEventSent?.();
    } catch (error) {
      console.error('Failed to dispatch event:', error);
    }
  };

  const renderField = (field: FieldDef) => {
    if (field.hidden) return null;
    if (field.dependsOn && !formData[field.dependsOn]) return null;

    const fieldLabel = t(field.labelKey);

    return (
      <div key={field.name} className="space-y-1.5">
        <Label htmlFor={field.name} className="text-sm">
          {fieldLabel}
          {field.required && <span className="text-destructive ml-0.5">*</span>}
        </Label>

        {field.type === 'store' ? (
          <StoreResourcePicker
            value={formData[field.name] || ''}
            onChange={(value) => handleFieldChange(field.name, value)}
          />
        ) : field.type === 'order' ? (
          <OrderResourcePicker
            storeId={formData[field.dependsOn!] || ''}
            value={formData[field.name] || ''}
            onChange={(order) => handleOrderPick(field, order)}
          />
        ) : field.type === 'select' ? (
          <Select
            value={formData[field.name] || ''}
            onValueChange={(value) => handleFieldChange(field.name, value)}
          >
            <SelectTrigger id={field.name} className="h-9">
              <SelectValue placeholder={t('simulator.selectPlaceholder', { label: fieldLabel.toLowerCase() })} />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>{t(opt.labelKey)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input
            id={field.name}
            type={field.type === 'number' ? 'number' : 'text'}
            placeholder={field.placeholder}
            value={formData[field.name] || ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            className="h-9"
          />
        )}

        {formErrors[field.name] && (
          <p className="text-destructive text-xs">{formErrors[field.name]}</p>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Category tabs */}
      <div className="flex flex-wrap gap-2">
        {CATEGORIES.map((cat) => (
          <Button
            key={cat}
            variant={selectedCategory === cat ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedCategory(cat)}
            className="h-8"
          >
            {t(`simulator.categories.${cat}`)}
            <Badge variant="secondary" className="ml-1.5 h-5 px-1.5 text-xs">
              {EVENT_REGISTRY.filter((e) => e.category === cat).length}
            </Badge>
          </Button>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-[240px_1fr]">
        {/* Event list sidebar */}
        <div className="space-y-1">
          {eventsInCategory.map((evt, idx) => (
            <button
              key={`${evt.namespace}/${evt.eventName}`}
              type="button"
              onClick={() => setSelectedEventIdx(idx)}
              className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors ${
                selectedEventIdx === idx
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-accent'
              }`}
            >
              <ChevronRight className={`h-3.5 w-3.5 shrink-0 ${selectedEventIdx === idx ? 'opacity-100' : 'opacity-0'}`} />
              <span className="truncate">{t(evt.labelKey)}</span>
            </button>
          ))}
        </div>

        {/* Event form */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-base">{t(eventDef.labelKey)}</CardTitle>
                <CardDescription className="mt-1">{t(eventDef.descriptionKey)}</CardDescription>
              </div>
              <Badge variant="outline" className="shrink-0 font-mono text-xs">
                {eventDef.namespace}/{eventDef.eventName}
              </Badge>
            </div>
          </CardHeader>

          <Separator />

          <CardContent className="pt-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              {eventDef.fields.map(renderField)}

              <Alert variant="default" className="mt-2">
                <Info className="h-4 w-4" />
                <AlertDescription className="text-xs" dangerouslySetInnerHTML={{ __html: t('simulator.realEventWarning') }} />
              </Alert>

              {lastSentEvent && (
                <div className="flex items-center gap-2 rounded-md bg-green-50 dark:bg-green-950/30 px-3 py-2 text-sm text-green-700 dark:text-green-400">
                  <CheckCircle2 className="h-4 w-4 shrink-0" />
                  <span dangerouslySetInnerHTML={{ __html: t('simulator.dispatched', { event: lastSentEvent }) }} />
                </div>
              )}

              <Button type="submit" disabled={dispatchEventMutation.isPending} className="w-full">
                {dispatchEventMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('simulator.dispatching')}
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    {t('simulator.dispatchButton', { label: t(eventDef.labelKey) })}
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
